import { useRef, useImperativeHandle, createElement, useState } from 'react';
import { Button, ControlLabel, Dialog, DialogContent, DialogRef, Info, LabelItem, Stack, Table, TableBody, TableCell, TableHead, TableRow } from 'src/app/components'
import { HookFormProvider, InputSelect, InputTextField } from 'src/app/components/HookForm'
import InputSelectMall from 'src/app/components/HookForm/InputSelect/InputSelect.mall'
import { SelectGiftFormModel, useSelectGiftByTransactionPopup } from './SelectGiftByTransaction.hook';
import InputNumberMinPlus from 'src/app/components/Inputs/InputNumberMinPlus/InputNumberMinPlus';
import { RefreshIcon } from 'src/app/components/Icons/ButtonIcons/RefreshIcon';
import { arrStringToString, safeArray } from 'src/app/utils/array-utils';
import { formatRupiahSpaced } from 'src/app/utils/input-utils';
import { SalesPromoSelectGiftPrizesModel } from 'src/app/modules/campaign/models/SalesPromo.model';
import { LoadingIcon } from 'src/app/components/Icons';
import { twMerge } from 'tailwind-merge';
import { Theme } from 'src/app/components/HookForm/HookForm/HookForm.theme';

interface SelectGiftPopupProps {
  dialogRef: React.RefObject<DialogRef>;
  save?: () => void;
  cancel?: () => void;
  id?: string; // <-- example of an additional prop you expect
  [key: string]: any; // <-- allows any other props (optional)
  params: { campaign_id?: string, member_id?: string, member_sales_promo_id?: string }
}

const isDisablePlus = (item?: SalesPromoSelectGiftPrizesModel) => {
  const qunatityTimesGift = ((item?.quantity || 0) * (item?.get_gift || 0))
  const max = (item?.max_gift || 0)
  let availability = max
  if (item?.type !== 'STAMP') {
    availability = Math.min((item?.remaining_quantity || 0), max)
  }
  return qunatityTimesGift >= availability
}

const SelectGiftByTransactionPopup = ({ dialogRef, save, cancel, params, ...props }: SelectGiftPopupProps) => {
  const localRef = useRef<DialogRef>(null);
  const state = useSelectGiftByTransactionPopup(params, clickCancel)

  const data = state.detail?.data
  useImperativeHandle(dialogRef, () => ({
    show: () => localRef.current?.show(
      state.detail.refetch()
    ),
    hide: () => localRef.current?.hide(),
  }), []);

  const submit = state.selectGiftForm.handleSubmit(async ({ ...formData }) => {
    const wrapUpDirectGift = formData.direct_prizes?.reduce<SalesPromoSelectGiftPrizesModel[]>(
      (prev, item) => {
        const existing = prev.find(obj => obj.id === item.id);
        if (existing && existing.quantity) {
          existing.quantity += item.quantity || 0;
        } else {
          prev.push({ ...item });
        }
        return prev;
      },
      [] // <-- this is the required initial value
    );
    const prizes = [...safeArray(wrapUpDirectGift), ...formData.prizes, ...(formData.extra_point_prize ? [formData.extra_point_prize] : [])].filter(el => el.quantity)
    const payload: SelectGiftFormModel = {
      claim_directory_id: formData.directory?.id,
      prizes
    }
    hitPostAPI(payload);
  })

  const hitPostAPI = async (payload: SelectGiftFormModel) => {
    try {
      await state.submit.mutateAsync(payload)
      clickCancel()
    } catch (error: any) {
      console.error(error)
    }
  }

  function clickCancel() {
    state.reset();
    props.params = {}
    cancel && cancel();
    localRef.current?.hide();
  }

  const [loadingAt, setLoadingAt] = useState<number>()

  const clickRandomize = async (index: number) => {
    const item = state.selectGiftForm.getValues(`direct_prizes.${index}`)
    const { random_pool_id } = item
    const payload = {
      random_pool_id,
      claim_directory_id: state.selectGiftForm.getValues('directory')?.id,
      member_sales_promo_id: params.member_sales_promo_id
    };
    setLoadingAt(index)
    try {
      const res = await state.directGift.shuffle.mutateAsync(payload);
      if (res?.data?.response_output?.detail) {
        const detail = { ...res?.data?.response_output?.detail, quantity: 1 };
        state.selectGiftForm.setValue(`direct_prizes.${index}`, detail);
        state.directGift.setDefaultDirectGiftVal(state.valDirectGift)
        setLoadingAt(undefined)
      }
    } catch (error: any) {
      console.error(error);
      setLoadingAt(undefined)
    }
  };

  const renderItemSummaryTable = (item: { name?: string, quantity?: number }, index: number) => {
    return (
      <TableRow key={`${item.name}-${index}`} className='last:border-0'>
        <TableCell value={item?.name} />
        <TableCell value={item?.quantity} />
      </TableRow>
    )
  }

  const renderItemDirectGiftTable = (item: SalesPromoSelectGiftPrizesModel, index: number) => {
    return (<TableRow key={`${index}directgift`} className='!border-0'>
      <TableCell className='w-[1em]'>&nbsp;</TableCell>
      <TableCell>
        <div className='flex gap-1 items-center justify-between w-full'>
          {state.isRandomize ?
            <>
              <InputTextField name={`direct_prizes.${index}.name`} disabled></InputTextField>
              <Button
                variant="outlined"
                className='!h-[40px] !w-[40px] !px-2 items-center'
                disabled={(!state.valClaimLocation || state.valDirectGift?.[index]?.is_locked) || loadingAt === index}
                onClick={() => clickRandomize(index)}
              >
                {(loadingAt === index) ? <LoadingIcon /> : createElement(RefreshIcon)}
              </Button>
            </> :
            <InputSelect
              placeholder="Select prize"
              className="w-full"
              isMulti={false}
              form={state.selectGiftForm}
              name={`direct_prizes.${index}`}
              options={state.directGift.directGiftOptions}
              getOptionLabel={(option) => option.name!}
              getOptionValue={(option) => option.id!}
              disabled={!state.valClaimLocation}
              onChangeValue={(selected: any) => state.directGift.changeValueDirectPrize(selected, index)} // store the object
            />
          }
        </div>
      </TableCell>
      <TableCell value={state.valDirectGift?.[index]?.remaining_quantity} colSpan={4} />
    </TableRow>)
  };

  const renderItemPrizeTable = (item: SalesPromoSelectGiftPrizesModel, index: number) => {
    return (
      <TableRow key={`${index}gift`} className='last:border-0'>
        <TableCell index={index + (state.hasDirectGift ? 1 : 0)} className='w-[1em]' />
        <TableCell value={item?.name} />
        <TableCell value={item?.remaining_quantity} />
        <TableCell value={item?.get_gift} />
        <TableCell value={item?.max_gift} />
        <TableCell contentWrapperClassName="justify-end">
          <InputNumberMinPlus min={0} disable={{ plus: isDisablePlus(state.valPrizes[index]) }} disabled={!state.valClaimLocation} name={`prizes.${index}.quantity`} />
        </TableCell>
      </TableRow>
    )
  }


  const renderDirectGift = () => {
    if (!state.hasDirectGift) return null;
    return (
      <TableRow>
        <TableCell index={0} className='w-[1em]' />
        <TableCell colSpan={2}>Direct Gift</TableCell>
        <TableCell value={state.directGift.directGiftRule?.get_gift} />
        <TableCell value={state.directGift.directGiftRule?.max_gift} />
        <TableCell contentWrapperClassName="justify-end">
          <InputNumberMinPlus min={0} max={Math.ceil((state.directGift.directGiftRule?.max_gift||1)/(state.directGift.directGiftRule?.get_gift||1))} disabled={!state.valClaimLocation} disable={{ plus: state.disableIncreaseRandomGift }} name="direct_prizes_quantity" onChangeVal={state.directGift.directGiftChange} />
        </TableCell>
      </TableRow>
    );
  };


  const renderExtraPoint = () => {
    if (!state.detail?.data?.prizes?.some((it: SalesPromoSelectGiftPrizesModel) => it.type === 'EXTRA_POINT')) return null;
    return (
      <TableRow>
        <TableCell index={state.gift.fields.length + 1} className='w-[1em]' />
        <TableCell value={state.valExtraPoint?.name} />
        <TableCell value={state.valExtraPoint?.remaining_quantity} />
        <TableCell value={state.valExtraPoint?.max_gift} />
        <TableCell value={state.valExtraPoint?.get_gift} />
        <TableCell contentWrapperClassName="justify-end">
          <InputTextField
            type='number'
            name={`extra_point_prize.quantity`}
            disabled={!state.valClaimLocation}
          />
        </TableCell>
      </TableRow>
    );
  };

  const tableRows = [
    renderDirectGift(),
    ...state.directGift.fields.map(renderItemDirectGiftTable),
    ...state.gift.fields.map(renderItemPrizeTable),
    renderExtraPoint(),
  ].filter(Boolean);

  return <Dialog
    size="lg"
    ref={localRef}
    title={`Receipt ID: ${data?.receipt_code}`}
    onHide={clickCancel}
  >
    <DialogContent>
      {(state.detail.isLoading || state.detail.isFetching) && <div className={twMerge(Theme.mask)}>
        <LoadingIcon />
      </div>}
      <Stack>
        <div className='mb-6'>
          <LabelItem className='gap-1' label='Amount Spending' value={data?.amount} renderValue={_ => _ ? formatRupiahSpaced(_) : undefined} />
          <LabelItem className='gap-1' label='Payment Category' value={arrStringToString(data?.payment_category_names)} />
          <LabelItem className='gap-1' label='Payment By' value={arrStringToString(data?.payment_methods_names)} />
          <LabelItem className='gap-1' label='Original Point Earned' value={data?.point} />
          {data?.is_combine_receipt && <>
            <LabelItem className='gap-1' label='Combine Receipt' value={arrStringToString(data?.receipt_codes)} />
            <LabelItem className='gap-1' label='Total Spend Combine Receipt' value={formatRupiahSpaced(data?.combine_amount)} />
          </>}

          {state.isRandomize ?
            <>
              {data?.is_multiplication && <Info
                className='mt-6'
                severity="gray"
                bordered={true}
                autoHide={false}
              >
                Multiplication rule applied: quantity will be multiplied by {data?.chances}.
              </Info>}
            </>
            : <LabelItem className='gap-1' label='Multiplication Applied' value={{ val: data?.is_multiplication, num: data?.chances || 0 }} renderValue={(_: { val?: boolean, num: number }) => _.val ? `Yes, Multiplied=${_.num}` : 'No'} />
          }
        </div>
      </Stack>
      <HookFormProvider
        form={state.selectGiftForm}
      >
        <Stack>
          <ControlLabel label="Claim Location">
            <InputSelectMall placeholder="Type to add" isMulti={false} form={state.selectGiftForm} rule={{ required: true }} />
          </ControlLabel>
        </Stack>
        <Stack className='pb-6 items-end' direction='col'>
          <div className="w-full border-2 px-3 rounded-lg">
            <Table classNames={{ container: 'mb-0 pb-0' }}>
              <TableHead>
                <TableRow>
                  <TableCell index className='w-[1em]' />
                  <TableCell>Gift/Voucher</TableCell>
                  <TableCell>Remaining Quota</TableCell>
                  <TableCell>Get Gift/Voucher</TableCell>
                  <TableCell>Max. Gift/Voucher</TableCell>
                  <TableCell contentWrapperClassName="justify-end">Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows}
              </TableBody>
            </Table>
          </div>
          <div className='border-2 px-3 rounded-lg'>
            <Table classNames={{ container: 'mb-0 pb-0' }}>
              <TableHead>
                <TableRow>
                  <TableCell>Prize Type</TableCell>
                  <TableCell>Total Qty Earned</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.summaryTable?.map(renderItemSummaryTable)}
              </TableBody>
            </Table>
          </div>
        </Stack>
      </HookFormProvider>
      <Stack
        direction="row"
        justify="between"
      >
        <Button variant='outlined' className='min-w-[128px]' onClick={clickCancel}>Cancel</Button>
        <Button className='min-w-[128px]' disabled={!state.valClaimLocation} loading={state.submit.isLoading} onClick={() => submit()}>Save</Button>
      </Stack>
    </DialogContent>
  </Dialog>
}
export default SelectGiftByTransactionPopup